<script context="module">
  export const prerender = true
</script>

<script>
  import { PixelatedContainer } from '$lib/components/containers'
  const clear = () => {
    if (!window.confirm('Delete all tracker saves and preferences from this browser? Export any saves you want to keep first. This cannot be undone and does not clear analytics or advertising cookies.'))
      return

    try {
      Object.keys(window.localStorage)
        .filter(key => key === 'nuzlocke' || key.startsWith('nuzlocke.'))
        .forEach(key => window.localStorage.removeItem(key))
    } catch {
      window.alert('Unable to finish clearing tracker data. Please use your browser settings to manage site storage.')
      return
    }

    window.alert('Tracker saves and preferences cleared. Analytics and advertising cookies have not been removed.')
    window.location.reload()
  }
</script>

<svelte:head>
  <title>Nuzlocke Tracker | Privacy Policy</title>
  <meta name="robots" content="noindex, nofollow" />
</svelte:head>

<main class='container mx-auto text-gray-800 pb-24 pt-8 sm:pt-0 dark:text-gray-300'>
  <div class='mx-auto pb-16 sm:pb-12 mx-4'>
    <PixelatedContainer className='my-16 py-6 px-8 overflow-x-hidden mx-auto max-w-prose'>
      <h1 class='text-2xl'>Privacy Policy</h1>
      <p class='text-xs -mt-px'>Last updated: October 5, 2026</p>

      <p>
        <b>Pokémon Nuzlocke Tracker</b> at nuzlocketracker.org is a free tool that
        you can use without creating an account or providing personal details.
        Your tracker data is stored only in your browser. We do not keep your
        save files or a user-account database on our servers.
      </p>
      <p>
        The only analytics and advertising services we use to collect information
        are <strong>Google Analytics 4 (GA4)</strong> and
        <strong>Google AdSense</strong>. GA4 helps us understand site usage;
        AdSense displays ads that help fund the site. Their data collection
        is explained separately below.
      </p>

      <h2 class='text-xl mb-2'>Local Saves and Preferences</h2>
      <p>
        Your runs, Pokémon, teams, and preferences are saved in your browser's
        local storage. This lets the tracker remember your progress between
        visits. Local storage is separate from the cookies used for analytics
        and advertising, and it is not a cloud backup.
      </p>

      <p>
        Tracker storage includes saved runs, Pokémon, teams, your active game,
        display preferences, and whether you have dismissed on-site notices.
        This data remains in your browser until you delete it or your browser
        clears it. Clearing site data,
        changing browsers, or using private browsing can cause you to lose saves.
        You can export backups from <a href='/saves'>Save files</a>.
      </p>

      <h2 class='text-xl mb-2'>Sharing Your Saves</h2>
      <p>
        A transfer link or QR code contains an encoded copy of your save.
        Anyone with the link can access that copy, so share it only with people
        you trust. When a transfer link is opened, its URL may be included in
        Google's analytics and advertising requests. Avoid including personal
        or sensitive information in save names or other fields you share.
      </p>

      <h2 class='text-xl mb-2'>Google Analytics 4</h2>
      <p>
        We use GA4 to measure visits and interactions and to improve the tracker.
        It can collect page URLs, referring pages, interaction events, browser
        and device information, language, and approximate location. Requests
        to Google also include technical information such as your IP address.
      </p>
      <p>
        GA4 uses first-party cookies, such as <code>_ga</code> and
        <code>_ga_*</code>, to distinguish browsers and sessions. These
        identifiers are separate from your tracker saves. See
        <a href='https://support.google.com/analytics/answer/11397207?hl=en'>Google's explanation of Analytics cookies</a>
        for their purposes and expiration settings.
      </p>

      <h2 class='text-xl mb-2'>Google AdSense and Advertising Cookies</h2>
      <p>
        We display advertisements through Google AdSense. Through AdSense,
        Google and its advertising partners may use cookies or similar technologies
        to deliver ads, measure their performance, limit repeated ads, and
        detect fraud. This can involve cookie identifiers, IP addresses,
        browser and device information, and interactions with ads.
      </p>
      <p>
        Google and its partners may use advertising cookies to personalize ads
        based on your visits to this site and other websites. The ads you see
        depend on factors including your privacy choices, location, and
        Google's settings. Non-personalized ads may still use cookies for
        measurement, frequency limits, and fraud prevention.
      </p>
      <p>
        Learn more about
        <a href='https://policies.google.com/technologies/partner-sites'>how Google uses information from sites that use its services</a>
        and read <a href='https://policies.google.com/privacy'>Google's Privacy Policy</a>.
      </p>

      <h2 id='privacy-choices' class='text-xl mb-2'>Your Privacy Choices</h2>
      <ul class='list-disc pl-5 mb-4 space-y-3'>
        <li>
          <strong>Google advertising:</strong> Visit
          <a href='https://adssettings.google.com/'>Google's ad settings</a>
          to manage or turn off personalized ads.
        </li>
        <li>
          <strong>AdSense advertising partners:</strong> Use
          <a href='https://optout.aboutads.info/'>WebChoices from the Digital Advertising Alliance</a>
          to opt out of interest-based advertising from participating companies.
        </li>
        <li>
          <strong>Google Analytics:</strong> Install the
          <a href='https://tools.google.com/dlpage/gaoptout'>Google Analytics opt-out browser add-on</a>
          in a supported browser to prevent Analytics from using your visits.
        </li>
        <li>
          <strong>Browser storage:</strong> Use your browser settings to manage
          or delete cookies and site storage. Blocking local storage can stop
          saves from working. Deleting cookies can also reset privacy choices,
          so you may need to make those choices again on each browser or device.
        </li>
      </ul>
      <p>
        Turning off personalized ads does not remove all ads or stop all
        measurement. The tracker's storage notice only acknowledges local
        storage for saves and preferences; it does not control Google's
        analytics or advertising cookies. Where a separate consent message
        is shown, use its controls to manage the choices it offers.
        Accepting our <a href='/terms-of-service'>Terms of Service</a> does not
        replace consent where consent is required.
      </p>

      <h2 class='text-xl mb-2'>Clear Local Tracker Data</h2>
      <p>
        You can <button type='button' onclick={clear} class='hover:cursor-pointer underline'>clear tracker saves and preferences from this browser</button>.
        Export any saves you want to keep first. This action cannot be undone.
        It does not delete analytics or advertising cookies, data already
        received by Google, exported files, or copies of shared saves.
      </p>

      <h2 class='text-xl mb-2'>Google Data Processing and Retention</h2>
      <p>
        Google processes analytics and advertising information as described
        in the policies linked above, and may process it in countries other
        than your own. Analytics retention depends on our GA4 property settings;
        advertising retention depends on the type of data and Google's policies.
        Clearing local tracker data does not erase information already
        received by Google.
      </p>
      <h2 class='text-xl mb-2'>Changes to This Policy</h2>
      <p>
        We may update this policy as the tracker or its service providers change.
        Updates will be posted here with a revised date. Where required, we
        will provide additional notice or request consent for new uses of data.
      </p>

      <h2 class='text-xl mb-2'>Contact Us</h2>
      <p>
        For privacy questions or data requests, email
        <a href='mailto:support@nuzlocketracker.org'>support@nuzlocketracker.org</a>.
      </p>
    </PixelatedContainer>
  </div>
</main>

<style lang="postcss">
  h2 { margin-top: 24px; }
  p { margin-bottom: 16px; }
  a { text-decoration: underline; text-underline-offset: 2px; overflow-wrap: anywhere; }
  a:hover, button:hover { text-decoration-thickness: 2px; }
</style>
